/**
 * Auto-scroll while narration streams — but only for a reader who is already at the bottom
 * (the story feed's progressive-narration checkbox).
 *
 * The rule is one sentence and the implementation is mostly about *when the answer is
 * recorded*. Pinned-ness is sampled on the reader's own scroll events, never at the moment we
 * are about to scroll: by then the last auto-scroll has already put the document at the
 * bottom, so asking "are they at the bottom?" would always answer yes and the reader could
 * never escape. Recording it on their input means one deliberate scroll up detaches the feed
 * for the rest of the turn, and scrolling back down re-attaches it.
 *
 * `overflow-anchor` does this natively in Chrome and Firefox and does not exist in Safari,
 * which is half our mobile traffic. This is four lines and works everywhere.
 *
 * **It is two hooks because the two halves have different lifetimes**, and collapsing them
 * into one is the bug this file shipped with. The question *"is the reader at the bottom?"*
 * belongs to the screen, which is mounted for the whole story; the answer to *"something grew,
 * follow it"* belongs to the thing that grows, which is a live turn and exists for fifty
 * seconds. When both lived in `LiveScene`, the mount-time sample ran with the live scene
 * already in the document — ~256px of chapter heading, echoed action, empty paragraph and
 * cursor — so a reader who was *exactly* at the bottom when they pressed the button measured
 * 256px away from it, `PINNED_THRESHOLD_PX` is 160, and the feed followed nobody, ever. The
 * `useRef(true)` default that would have been right was overwritten by a measurement of our
 * own content. jsdom cannot see it (no layout means `scrollHeight` is 0 and everyone is pinned
 * by arithmetic); `tests/e2e/feed.spec.ts` is what caught it and is what holds it.
 */
import { useEffect, useRef, type RefObject } from 'react';
import { followStep, isPinnedToBottom } from '@/features/story/scroll';

/**
 * Track where the reader is. **Mount this on the screen, not on the thing that grows.**
 *
 * The returned ref is deliberately a ref and not state: nothing renders differently because of
 * it, it is read once inside an effect at the moment content lands, and making it state would
 * re-render the whole story screen on every scroll event of a fifty-second turn.
 *
 * **Only an *upward* scroll detaches the feed**, and that asymmetry is what lets the follow
 * below be a glide rather than a jump. The follow now trails the bottom by however much it has
 * left to close, and every frame of it fires a scroll event that this sampler sees; measured
 * on distance alone, the feed's own easing would look exactly like a reader who had wandered
 * off and would switch itself off within a frame or two of starting. Direction tells the two
 * apart with nothing shared between the hooks: we only ever scroll *towards* the bottom, so
 * anything moving away from it is a person. Coming back within `PINNED_THRESHOLD_PX` of the
 * bottom re-attaches, however they got there.
 *
 * @param ready whether there is a story on screen to be at the bottom *of*. The story screen's
 * first commit renders "Loading the story…" — a document shorter than the viewport, where
 * everybody is trivially pinned — so sampling then and never again (the shape this hook had,
 * one component down) records an answer about a page that no longer exists. Passing the flag
 * re-runs the sample against the real feed the moment there is one.
 */
export function useReaderPinning(ready: boolean): RefObject<boolean> {
  // Assume pinned until told otherwise: a story short enough not to scroll, and the opening
  // turn of a story with no chapters at all, are both "at the bottom" and neither fires a
  // scroll event to say so. `useOpenAtEnd` has also just put a returning reader there.
  const pinned = useRef(true);

  useEffect(() => {
    if (!ready) return;

    let last = window.scrollY;

    const sample = (): void => {
      const scrollTop = window.scrollY;
      // A pixel of slack: sub-pixel scroll positions and a browser's own clamping both
      // produce tiny negative deltas that are nobody's decision.
      const wentUp = scrollTop < last - 1;
      last = scrollTop;

      if (
        isPinnedToBottom({
          scrollTop,
          viewport: window.innerHeight,
          content: document.documentElement.scrollHeight,
        })
      ) {
        pinned.current = true;
        return;
      }

      // Away from the bottom, but not by their hand — the feed easing down behind growing
      // text, or a resize. Leaving the answer alone is what keeps the follow alive.
      if (wentUp) pinned.current = false;
    };

    sample();
    window.addEventListener('scroll', sample, { passive: true });
    window.addEventListener('resize', sample, { passive: true });
    return () => {
      window.removeEventListener('scroll', sample);
      window.removeEventListener('resize', sample);
    };
  }, [ready]);

  return pinned;
}

/**
 * Follow the content down the page as it grows, for a reader `useReaderPinning` says is at the
 * bottom of it.
 *
 * **It eases rather than jumps, and that is the whole of the reading complaint it answers.**
 * The first version set `scrollTo(scrollHeight)` on every trigger, which is correct and
 * horrible: narration arrives in bursts, so the page lurched a sentence at a time, and the line
 * the reader was on left the screen with no motion to follow it by. Closing a fraction of the
 * gap per animation frame (`followStep`) makes the same stream one continuous glide — the eye
 * tracks it the way it tracks a scrolling credit roll instead of re-finding its place after
 * every burst.
 *
 * Three things keep the glide from becoming a fight:
 *
 * - **The loop re-reads the page every frame**, both where it is (`window.scrollY`) and where
 * the bottom is: content is arriving underneath it, so a target computed once is stale by the
 * time it is reached.
 * - **It gives up the instant `pinned` goes false**, mid-flight, rather than finishing the
 * animation it started. A reader who scrolls up during a glide is answering the question the
 * glide was asking.
 * - **`prefers-reduced-motion` gets the jump**, which is the one case where the old behaviour
 * is the right one: someone who has asked for no animation is asking for the destination.
 *
 * This is still not a typewriter and must not become one — nothing here delays *text*
 * (`useFrameBuffer` publishes every byte the frame it arrives); it only decides how fast the
 * viewport chases it.
 *
 * @param pinned the screen's answer, which outlives this hook's component on purpose.
 * @param trigger changes whenever there is new content to follow — the *rendered* narration
 * length and the change count. Any value works; only its identity is read. It must be what the
 * DOM actually holds rather than what the store holds: narration is published to the view once
 * per animation frame (`useFrameBuffer`), so a trigger taken from the store fires a frame
 * before the text it is meant to be following exists, and the feed ends a turn one delta short.
 * @param active false when no turn is streaming, which is when the feed must not move at all.
 */
export function useStickToBottom(
  pinned: RefObject<boolean>,
  trigger: unknown,
  active: boolean,
): void {
  useEffect(() => {
    if (!active || !pinned.current) return;

    const bottom = (): number =>
      Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

    // No rAF (jsdom without `pretendToBeVisual`), or no animation wanted: land on the answer.
    if (typeof requestAnimationFrame !== 'function' || prefersReducedMotion()) {
      window.scrollTo({ top: bottom(), behavior: 'auto' });
      return;
    }

    let frame: number | null = null;

    const step = (): void => {
      frame = null;
      // Re-checked every frame rather than only at the start: the reader can leave in the
      // middle of a glide, and continuing would be scrolling a page they took back.
      if (!pinned.current) return;

      const to = bottom();
      const next = followStep(window.scrollY, to);
      // `auto` on each frame, never `smooth`: the browser's own smooth scroll re-targets on
      // every call, so fifty seconds of per-frame calls is an animation that never arrives.
      window.scrollTo({ top: next, behavior: 'auto' });
      if (next < to) frame = requestAnimationFrame(step);
    };

    step();

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [pinned, trigger, active]);
}

/** `matchMedia` is absent in jsdom, where nothing moves anyway. */
function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
